import React from 'react';
import {
 Card, CardContent
} from 'shared/components/ui/card';
import {
 Badge
} from 'shared/components/ui/badge';
import {
 Button
} from 'shared/components/ui/button';
import {
 Input
} from 'shared/components/ui/input';
import {
 Label
} from 'shared/components/ui/label';
import {
 ChevronDown, ChevronRight, Trash2, Plus
} from 'lucide-react';
import {
    WorkflowStep, WorkflowStepType, FetchStepConfig, ApiCallStepConfig, MutateStepConfig,
} from '../../types/workflow.types';
import FetchStepForm from './FetchStepForm';
import ApiCallStepForm from './ApiCallStepForm';
import MutateStepForm from './MutateStepForm';

interface WorkflowStepCardProps {
    step: WorkflowStep;
    stepIndex: number;
    isExpanded: boolean;
    isLast: boolean;
    allSteps: WorkflowStep[];
    onToggleExpand: () => void;
    onUpdate: (data: Partial<WorkflowStep>) => void;
    onUpdateConfig: (config: FetchStepConfig | ApiCallStepConfig | MutateStepConfig) => void;
    onDelete: () => void;
    onAddAfter: () => void;
}

const stepTypeColors: Record<WorkflowStepType, string> = {
    FETCH: 'bg-blue-100 text-blue-700 border-blue-200',
    API_CALL: 'bg-amber-100 text-amber-700 border-amber-200',
    MUTATE: 'bg-green-100 text-green-700 border-green-200',
};

const stepTypeLabels: Record<WorkflowStepType, string> = {
    FETCH: 'Fetch Data',
    API_CALL: 'API Call',
    MUTATE: 'Mutate Data',
};

const defaultConfigs: Record<WorkflowStepType, FetchStepConfig | ApiCallStepConfig | MutateStepConfig> = {
    FETCH: {
        schemaName: '',
        fields: [],
        filters: [],
        limit: 100,
        orderBy: '',
        orderDirection: 'asc',
    },
    API_CALL: {
        connectionId: '',
        method: 'GET',
        path: '',
        headers: {},
        body: '',
    },
    MUTATE: {
        operation: 'INSERT',
        schemaName: '',
        fieldMappings: [],
        condition: '',
    },
};

const WorkflowStepCard: React.FC<WorkflowStepCardProps> = ({
    step, stepIndex, isExpanded, isLast, allSteps,
    onToggleExpand, onUpdate, onUpdateConfig, onDelete, onAddAfter,
}) => {
    const handleTypeChange = (newType: WorkflowStepType) => {
        if (newType === step.type) return;
        onUpdate({
 type: newType,
config: defaultConfigs[newType]
});
    };

    return (
        <div className="relative">
            {/* Step indicator + connector */}
            <div className="flex items-start gap-3">
                {/* Vertical connector line + circle */}
                <div className="flex flex-col items-center shrink-0">
                    <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center text-xs font-bold ${
                        stepTypeColors[step.type]
                    }`}>
                        {stepIndex + 1}
                    </div>
                    {!isLast && (
                        <div className="w-0.5 h-full bg-border min-h-[20px]" />
                    )}
                </div>

                {/* Card */}
                <Card className="flex-1 mb-2">
                    {/* Header - always visible */}
                    <button
                        type="button"
                        className="flex items-center w-full p-3 gap-3 hover:bg-muted/30 transition-colors text-left"
                        onClick={onToggleExpand}
                    >
                        {isExpanded
                            ? <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                            : <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />}
                        <Badge variant="secondary" className={`text-xs shrink-0 ${stepTypeColors[step.type]}`}>
                            {step.type.replace('_', ' ')}
                        </Badge>
                        <span className="font-medium text-sm truncate flex-1">
                            {step.name || stepTypeLabels[step.type]}
                        </span>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-red-500 hover:text-red-700 shrink-0"
                            onClick={(e) => { e.stopPropagation(); onDelete(); }}
                        >
                            <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                    </button>

                    {/* Expanded content */}
                    {isExpanded && (
                        <CardContent className="pt-0 pb-4 px-4 space-y-4 border-t">
                            {/* Step Name */}
                            <div className="space-y-1.5 pt-3">
                                <Label className="text-sm">Step Name</Label>
                                <Input
                                    value={step.name}
                                    onChange={(e) => onUpdate({
 name: e.target.value
})}
                                    placeholder={stepTypeLabels[step.type]}
                                />
                            </div>

                            {/* Step Type Selector */}
                            <div className="space-y-1.5">
                                <Label className="text-sm">Step Type</Label>
                                <div className="grid grid-cols-3 gap-2">
                                    {(['FETCH', 'API_CALL', 'MUTATE'] as const).map((type) => (
                                        <button
                                            key={type}
                                            type="button"
                                            className={`p-2 border rounded-md text-center text-sm font-medium transition-colors ${
                                                step.type === type
                                                    ? stepTypeColors[type]
                                                    : 'border-muted hover:bg-muted/50'
                                            }`}
                                            onClick={() => handleTypeChange(type)}
                                        >
                                            {stepTypeLabels[type]}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Config Form */}
                            <div className="border-t pt-4">
                                {step.type === 'FETCH' && (
                                    <FetchStepForm
                                        config={step.config as FetchStepConfig}
                                        onChange={onUpdateConfig}
                                    />
                                )}
                                {step.type === 'API_CALL' && (
                                    <ApiCallStepForm
                                        config={step.config as ApiCallStepConfig}
                                        onChange={onUpdateConfig}
                                        steps={allSteps}
                                        stepIndex={stepIndex}
                                    />
                                )}
                                {step.type === 'MUTATE' && (
                                    <MutateStepForm
                                        config={step.config as MutateStepConfig}
                                        onChange={onUpdateConfig}
                                        steps={allSteps}
                                        stepIndex={stepIndex}
                                    />
                                )}
                            </div>
                        </CardContent>
                    )}
                </Card>
            </div>

            {/* Add step button between steps */}
            <div className="flex items-center gap-3 py-1">
                <div className="w-8 flex justify-center shrink-0">
                    <div className="w-0.5 h-6 bg-border" />
                </div>
                <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs text-muted-foreground hover:text-foreground"
                    onClick={onAddAfter}
                >
                    <Plus className="mr-1 h-3 w-3" /> Add Step
                </Button>
            </div>
        </div>
    );
};

export default WorkflowStepCard;
